import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CalendarClock, Clock, Smile } from 'lucide-react';
import type { CSSProperties } from 'react';
import { useSearchParams } from 'react-router';
import { dashboardApi } from '../api/admin';
import {
  BarList,
  Card,
  ColumnChart,
  CountUp,
  EmptyState,
  ErrorBanner,
  PageHeader,
  PageSkeleton,
  SegmentedControl,
  StatTile,
  TalkRatio,
  Table,
  Td,
  Th,
  Tr,
} from '../components';
import { cn } from '../lib/cn';
import { RANGE_PRESETS, presetRange, type RangePreset } from '../lib/dates';
import { formatDate, formatDuration, formatNumber, formatShortDate } from '../lib/format';
import { OUTCOME_LABELS, SENTIMENT_LABELS } from '../lib/labels';
import { ClientHealthCard } from './dashboard/ClientHealthCard';

const stagger = (i: number) => ({ '--i': i }) as CSSProperties;

export function DashboardPage() {
  const [sp, setSp] = useSearchParams();
  const preset = (RANGE_PRESETS.find((p) => p.value === sp.get('range'))?.value ?? 'month') as RangePreset;
  const range = presetRange(preset);
  const query = useQuery({
    queryKey: ['dashboard', range.from, range.to],
    queryFn: () => dashboardApi.get(range.from, range.to),
    placeholderData: (prev) => prev,
  });
  const d = query.data;

  // `clientHealth` and `previous` are missing on servers older than this page.
  const health = d?.clientHealth;
  const clientsMet = health ? health.positive + health.neutral + health.negative + health.noData : 0;
  const change =
    d?.previous && d.previous.meetings > 0 ? Math.round(((d.totals.meetings - d.previous.meetings) / d.previous.meetings) * 100) : null;

  return (
    <div>
      <PageHeader
        title="Dashboard"
        description={d ? `${formatDate(d.range.from)} – ${formatDate(d.range.to)}` : undefined}
        actions={
          <SegmentedControl
            label="Date range"
            value={preset}
            options={RANGE_PRESETS.map((p) => ({ value: p.value, label: p.label }))}
            onChange={(v) => setSp(v === 'month' ? {} : { range: v }, { replace: true })}
          />
        }
      />

      {query.isError && <ErrorBanner error={query.error} onRetry={() => void query.refetch()} className="mb-4" />}
      {query.isLoading && <PageSkeleton />}

      {d && (
        <div className={cn('space-y-4 transition-opacity', query.isPlaceholderData && 'opacity-60')}>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <StatTile
              lead
              style={stagger(0)}
              label="Clients at risk"
              icon={<AlertTriangle aria-hidden className="h-4 w-4" />}
              value={health ? <CountUp value={health.negative} /> : '—'}
              sub={health ? `of ${formatNumber(clientsMet)} ${clientsMet === 1 ? 'client' : 'clients'} met` : undefined}
            />
            <StatTile
              style={stagger(1)}
              label="Average sentiment"
              icon={<Smile aria-hidden className="h-4 w-4" />}
              value={health?.average ? SENTIMENT_LABELS[health.average] : '—'}
              sub="across clients met"
            />
            <StatTile
              style={stagger(2)}
              label="Meetings"
              icon={<CalendarClock aria-hidden className="h-4 w-4" />}
              value={<CountUp value={d.totals.meetings} />}
              sub={change === null ? 'no meetings in the previous period' : `${change >= 0 ? '+' : ''}${change}% vs previous period`}
            />
            <StatTile
              style={stagger(3)}
              label="Call time"
              icon={<Clock aria-hidden className="h-4 w-4" />}
              value={formatDuration(d.totals.totalDurationSec)}
              sub={`${formatDuration(d.totals.avgDurationSec)} average`}
            />
          </div>

          <div className="grid gap-4 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
            <ClientHealthCard health={health} clients={d.clientsNeedingAttention ?? []} style={stagger(4)} />
            <Card title="Meetings per week" style={stagger(5)}>
              {d.weekly.length === 0 ? (
                <EmptyState title="No meetings in this range" />
              ) : (
                <div className="pt-4">
                  <ColumnChart
                    data={d.weekly.map((w) => ({ key: w.weekStart, label: formatShortDate(w.weekStart), value: w.meetings }))}
                    tableLabels={{ key: 'Week starting', value: 'Meetings' }}
                  />
                </div>
              )}
            </Card>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card title="Meetings per rep" bodyClassName="px-2 py-2" style={stagger(6)}>
              {d.perRep.length === 0 ? (
                <EmptyState title="No meetings in this range" />
              ) : (
                <Table>
                  <thead>
                    <tr>
                      <Th>Rep</Th>
                      <Th className="text-right">Meetings</Th>
                      <Th className="text-right">Call time</Th>
                      <Th className="text-right">Avg call</Th>
                      <Th>Talk ratio (me)</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...d.perRep]
                      .sort((a, b) => b.meetings - a.meetings)
                      .map((r) => (
                        <Tr key={r.repId}>
                          <Td>{r.repName}</Td>
                          <Td className="text-right tabular-nums">{formatNumber(r.meetings)}</Td>
                          <Td className="text-right tabular-nums">{formatDuration(r.durationSec)}</Td>
                          <Td className="text-right tabular-nums">{r.meetings ? formatDuration(r.durationSec / r.meetings) : '—'}</Td>
                          <Td>
                            <TalkRatio ratio={r.talkRatioMe} />
                          </Td>
                        </Tr>
                      ))}
                  </tbody>
                </Table>
              )}
            </Card>
            <Card title="Most common objections" style={stagger(7)}>
              {d.topObjections.length === 0 ? (
                <EmptyState title="No objections recorded" />
              ) : (
                <BarList data={d.topObjections.map((o, i) => ({ key: `${i}-${o.text}`, label: o.text, value: o.count }))} />
              )}
            </Card>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card title="Outcomes" style={stagger(8)}>
              {d.outcomes.length === 0 ? (
                <EmptyState title="No meetings in this range" />
              ) : (
                <BarList
                  data={[...d.outcomes]
                    .sort((a, b) => b.count - a.count)
                    .map((o) => ({ key: o.outcome, label: o.outcome === 'none' ? 'Not set' : OUTCOME_LABELS[o.outcome], value: o.count }))}
                />
              )}
            </Card>
            <Card title="Terms reps most needed explained" style={stagger(9)}>
              {d.topTerms.length === 0 ? (
                <EmptyState title="No term tips in this range" />
              ) : (
                <BarList data={d.topTerms.map((t, i) => ({ key: `${i}-${t.term}`, label: t.term, value: t.count }))} />
              )}
            </Card>
          </div>

          <p className="text-xs text-muted">
            Tip feedback: 👍 {formatNumber(d.tipFeedback.up)} / 👎 {formatNumber(d.tipFeedback.down)} of {formatNumber(d.tipFeedback.total)} tips shown
          </p>
        </div>
      )}
    </div>
  );
}
